import React from 'react'
import styles from './PaymentForm.scss'
import {RadioButtonGroup} from 'common/styleguide/RadioGroup'
import {CreditCardForm} from '../CreditCardForm'
import {TotalCalculator} from '../TotalCalculator'
import {UpdatePayment} from '../UpdatePayment'
import {PaymentSuccess} from '../PaymentSuccess'
import {UpsellFooter} from './UpsellFooter'
import {Hero} from 'common/styleguide/Hero/Hero'
import {CTA} from 'common/styleguide/CTA'
import {errorMessage} from 'lib/styleguide/base.scss'
import {Map} from 'immutable'
import {getCurrentUser} from 'lib/utils'
import fetchJSON from 'lib/fetchJSON'
import queryString from 'query-string'
import {CircleSpinner} from 'common/spinners'
import moment from 'moment'

const APPLE_PAYMENT_TYPE = 3

const paymentStatusEnum = {
  NONE: 'none',
  PENDING: 'pending',
  SUCCESS: 'success',
  DISABLED: 'disabled'
}

// NOTE: baseCostMatrix as returned from the server looks like accountLevelString -> term -> cost in cents
// for ex:
// const baseCostMatrix = {
//   basic: {
//     monthly: 600,
//     yearly: 5000
//   },
//   yearly: {
//     monthly: 1000,
//     yearly: 8000
//   }
// }

// accountLevel -> Map(term -> <span>)
const paymentTermOptions = (matrix) => {
  const basicMonthly = matrix['basic']['monthly'] / 100
  const basicYearly = matrix['basic']['yearly'] / 100
  const basicSaved = Math.round(100 * (1 - basicYearly / (basicMonthly * 12)))
  const premiumMonthly = matrix['premium']['monthly'] / 100
  const premiumYearly = matrix['premium']['yearly'] / 100
  const premiumSaved = Math.round(100 * (1 - premiumYearly / (premiumMonthly * 12)))

  return {
    1: Map({
      yearly: <span className={styles.boldLabel}>
        <span>${basicYearly} (USD) yearly</span>
        <span className={styles.saved}>Save {basicSaved}% a year</span>
      </span>,
      monthly: <span className={styles.boldLabel}>${basicMonthly} (USD) monthly</span>
    }),
    2: Map({
      yearly: <span className={styles.boldLabel}>
        <span>${premiumYearly} (USD) yearly</span>
        <span className={styles.saved}>Save {premiumSaved}% a year</span>
      </span>,
      monthly: <span className={styles.boldLabel}>${premiumMonthly} (USD) monthly</span>
    })
  }
}

export class PaymentForm extends React.PureComponent {
  static defaultProps = {
    currentUser: getCurrentUser()
  }

  constructor (props) {
    super(props)

    this.state = {
      fetchPending: true,
      selectedTerm: 'yearly',
      showCCForm: true,
      paymentStatus: paymentStatusEnum.NONE
    }
  }

  componentDidMount = () => {
    const {currentUser} = this.props
    let [accountLevel, accountLevelString] = this.parseParams()

    var fetch = fetchJSON(`/users/${currentUser.id}/upgrading_subscription.json?apikey=x&version=3&accountLevel=${accountLevelString}`)
    Promise.all([fetch, this.injectBraintree()])
      .then(([res, _]) => {
        const ps = res.page_state
        if (res.meta.subscription_present) {
          this.setState({
            monthlyRateAfterDiscounts: ps.monthly_rate_after_discounts,
            yearlyRateAfterDiscounts: ps.yearly_rate_after_discounts,
            isApple: ps.subscription.payment_method_type === APPLE_PAYMENT_TYPE
          })
        }

        if (ps.subscription && ps.subscription.payment_method_type === APPLE_PAYMENT_TYPE) {
          this.setState({braintreeToken: ps.braintree_token})
        } else {
          this.setupBraintree(ps.braintree_token)
        }

        const baseCostFromRes = res.meta.base_cost_grid
        this.setState({
          baseCostMatrix: baseCostFromRes,
          baseCost: baseCostFromRes[accountLevelString][this.state.selectedTerm] / 100,
          yearlyQuote: ps.yearly_quote,
          yearlyNextBillDate: moment(ps.yearly_next_bill_date),
          monthlyQuote: ps.monthly_quote,
          monthlyNextBillDate: moment(ps.monthly_next_bill_date),
          subscriptionCredit: ps.credit,
          daysLeft: ps.days_left
        })

        if (res.meta.discount_grid_present) {
          this.setState({discountLineItems: ps.discount_grid})
        }
      })
      .catch(res => {
        console.error(res)
        this.setState({
          error: res.errors[0].error_message,
          paymentStatus: paymentStatusEnum.DISABLED
        })
      })
    this.setState({
      accountLevel: accountLevel,
      accountLevelString: accountLevelString,
      fetchPending: false
    })
  }

  injectBraintree = () => new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://js.braintreegateway.com/v2/braintree.js'
    script.onload = () => resolve(true)
    document.body.appendChild(script)
  })

  setupBraintree = (token) => {
    window.braintree.setup(token, 'custom', {
      id: 'paymentForm',
      onPaymentMethodReceived: (res) => {
        let cn = res.type !== 'PayPalAccount' && document.getElementById('cardNumber').value
        if (res.type === 'CreditCard' && (cn === null || cn.length === 0)) {
          this.setState({
            error: 'Please enter a valid card number'
          })
        } else {
          this.submitForm(res.nonce)
        }
      },
      paypal: {
        container: 'paypalContainer',
        onSuccess: () => { this.setState({showCCForm: false}) },
        onCancelled: () => { this.setState({showCCForm: true}) }
      }
    })
  }

  parseParams = () => {
    const accountLevelParam = queryString.parse(this.props.location.search).account_level
    return accountLevelParam === 'premium' ? [2, accountLevelParam] : [1, accountLevelParam]
  }

  onRadioChange = (option) => {
    const {accountLevelString, baseCostMatrix} = this.state

    this.setState({
      baseCost: baseCostMatrix[accountLevelString][option] / 100,
      selectedTerm: option
    })
  }

  appleContinue = () => {
    // HACK: setupBraintree fails if isApple === true when it's run (bc the form isn't shown)
    const {braintreeToken} = this.state
    this.setupBraintree(braintreeToken)

    this.setState({isApple: false})
  }

  submitForm = (paymentMethodNonce) => {
    const {userId, selectedTerm} = this.state

    const body = {
      user_id: userId,
      billing_type: selectedTerm,
      payment_method_nonce: paymentMethodNonce
    }

    this.setState({paymentStatus: paymentStatusEnum.PENDING})
    fetchJSON(`/subscriptions/confirmation`, body, {method: 'POST'})
      .then(() => {
        this.setState({paymentStatus: paymentStatusEnum.SUCCESS})
      })
      .catch(res => {
        console.error(res)
        this.setState({
          error: res.error,
          paymentStatus: paymentStatusEnum.NONE
        })
      })
  }

  render () {
    const {
      error,
      fetchPending,
      showCCForm,
      paymentStatus,
      isApple,
      discountLineItems,
      accountLevel,
      baseCost,
      baseCostMatrix,
      selectedTerm,
      subscriptionCredit,
      daysLeft,
      yearlyQuote,
      yearlyNextBillDate,
      monthlyQuote,
      monthlyNextBillDate} = this.state

    const {currentUser} = this.props

    const showForm = !(fetchPending ||
                       isApple ||
                       paymentStatus === paymentStatusEnum.SUCCESS)
    const paymentDisabled = [paymentStatusEnum.DISABLED,
      paymentStatusEnum.PENDING].indexOf(paymentStatus) >= 0
    const upgrading = (currentUser.accountLevel < accountLevel && subscriptionCredit > 0)

    let ctaText = accountLevel === 2 ? 'Upgrade to Premium' : 'Upgrade to Basic'

    return (
      <div className={styles.PaymentForm}>
        <Hero
          img={'/images/revised_layout/signup-bg.jpg'}
          img2x={'/images/revised_layout/signup-bg.jpg'}
          marginBottom={'0px'}
          blur
          className={styles.hero}>
          {fetchPending && <CircleSpinner centered size={48} className={styles.centeredSpinner} />}
          {isApple && <UpdatePayment accountLevel={accountLevel} appleContinue={this.appleContinue} />}
          {paymentStatus === paymentStatusEnum.SUCCESS && <PaymentSuccess />}
          {showForm && <div className={styles.container}>
            <div className={styles.left}>
              {error && <p className={errorMessage}>{error}</p>}
              <h2>Upgrade Your Account</h2>
              <div className={styles.line} />
              {upgrading && <div className={styles.explainer}>Upgrade to a {accountLevel === 2 ? 'Premium' : 'Basic'} subscription
              today, and we'll prorate the charge to reflect your remaining subscription term.</div>}
              <form id='paymentForm'>
                <div className={styles.rBGContainer}>
                  {baseCostMatrix && <RadioButtonGroup
                    options={paymentTermOptions(baseCostMatrix)[accountLevel]}
                    selected={selectedTerm}
                    onChange={this.onRadioChange} />}
                </div>

                {showCCForm && <CreditCardForm />}

                <div className={styles.goButtons}>
                  <CTA disabled={paymentDisabled} className={styles.topGoButton} button>
                    {paymentStatus === paymentStatusEnum.PENDING
                      ? <CircleSpinner light size={12} />
                      : ctaText}
                  </CTA>
                  {showCCForm && <span>or</span>}
                  <div id='paypalContainer' className={styles.paypalContainer} />
                </div>
              </form>
            </div>
            <div className={styles.right}>
              <TotalCalculator
                accountLevelString={accountLevel === 1 ? 'basic' : 'premium'}
                term={selectedTerm}
                quote={selectedTerm === 'yearly' ? yearlyQuote : monthlyQuote}
                nextBillDate={selectedTerm === 'yearly' ? yearlyNextBillDate : monthlyNextBillDate}
                subscriptionCredit={subscriptionCredit}
                daysLeft={daysLeft}
                baseCost={baseCost}
                discountLineItems={discountLineItems} />
            </div>
          </div>}
        </Hero>
        <UpsellFooter accountLevel={accountLevel} />
      </div>
    )
  }
}
